import { IpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import type { Filters, TableRequestBody } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import type { ApiTableMergedState } from 'components/ApiTable/model/ApiTableModel';
import { option } from 'fp-ts';
import { isEmpty } from 'fp-ts/Record';
import { pipe } from 'fp-ts/function';
import { createSelector } from 'reselect';
import { createScopedSelector } from 'utils/react/scopedSelector';
import { trimEntity } from 'utils/trimEntity';

const getFiltersWithStaticFilters =
  (filters: Filters) =>
  (staticFilters: Filters): Filters =>
    !isEmpty(filters) ? { and: [filters, staticFilters] } : staticFilters;

const getRequestFilters = (
  filters: Filters,
  staticFilters?: Filters,
): Filters => {
  const trimmedFilters = trimEntity(filters);

  return pipe(
    staticFilters,
    option.fromNullable,
    option.map(getFiltersWithStaticFilters(trimmedFilters)),
    option.getOrElse(() => trimmedFilters),
  );
};

export const selectTableRequestPayloadBase: (
  s: ApiTableMergedState,
) => Pick<
  TableRequestBody<any>,
  'attributeFilters' | 'bindVariables' | 'filters' | 'snapshot' | 'sort'
> = createScopedSelector(() =>
  createSelector(
    prop('filters'),
    prop('sort'),
    (s) =>
      s.endpoint.requireAttributeFilters ? s.attributeFilters : undefined,
    prop('bindVariables'),
    prop('staticFilters'),
    (s) => {
      if (s.endpoint.snapshot === 'required') {
        if (s.snapshotId === null) {
          throw new IpfError(
            'INVALID_ARGUMENT',
            'Selected snapshot is required to display this table',
          );
        }
        return s.snapshotId ?? undefined;
      }
      return undefined;
    },
    (
      filters,
      sort,
      attributeFilters,
      bindVariables,
      staticFilters,
      snapshot,
    ) => ({
      attributeFilters,
      bindVariables,
      filters: getRequestFilters(filters, staticFilters),
      snapshot,
      sort,
    }),
  ),
);
